/**
 * HTTP for the Buddy chat-flow transport: the runner front door (bootstrap /
 * turn / state / preset-turn) plus the attachment upload. Ported from the customer
 * widget's `api/endpoints.ts` — apps/web cannot import apps/chat-v1, so the wire
 * shapes are duplicated here and must stay in step with it.
 */
import type { BuddyFlowConfig } from './buddy-flow-config'
import { getBuddyFlowConfig, resolveFlowBase } from './buddy-flow-config'
import type { BuddyFlowCustomData } from './buddy-flow-context'
import { buildFlowSignedHeaders } from './buddy-flow-signing'

/** A message the bootstrap walk already produced, in the same shape as a stream frame. */
export interface BuddyFlowPreMessage {
  type: string
  content: Record<string, unknown>
}

export interface BuddyFlowEnvelope {
  session_id: string
  agent_url?: string | null
  pre_ai_messages?: BuddyFlowPreMessage[]
  bootstrap_state: 'ai' | 'terminal' | 'human' | 'waiting' | 'preset_wait'
  end_message?: string
  grant_token?: string
  channel?: string
  reply_deadline_at?: number
  ai_speaks_first?: boolean
  waiting_message?: string
}

/** An uploaded attachment as the flow expects to receive it. */
export interface BuddyFlowAttachmentRef {
  file_url: string
  file_name: string
  mime_type: string
}

export class BuddyFlowError extends Error {
  status: number

  code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

// The first walk after an idle window can run ~16s while the runner's connection
// pool heals, so the widget's 25s headroom is carried over verbatim.
const BOOTSTRAP_TIMEOUT_MS = 25_000
const RUNNER_READ_TIMEOUT_MS = 15_000

const signedHeaders = (config: BuddyFlowConfig, url: string, method: string) =>
  buildFlowSignedHeaders({
    url,
    method,
    appId: config.appId,
    appKey: config.appKey,
    clientType: config.clientType,
    region: config.region,
    timestamp: Date.now(),
  })

const runnerUrl = (config: BuddyFlowConfig, path: string): string =>
  `${resolveFlowBase(config.runnerBaseUrl, config.region)}${path}`

const readErrorEnvelope = async (response: Response, fallback: string): Promise<BuddyFlowError> => {
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string }
  } | null
  return new BuddyFlowError(
    response.status,
    body?.error?.code || 'INTERNAL',
    body?.error?.message || fallback,
  )
}

const runnerFetch = async (
  config: BuddyFlowConfig,
  path: string,
  init: { method: 'GET' | 'POST'; body?: unknown; timeoutMs?: number },
): Promise<BuddyFlowEnvelope> => {
  const url = runnerUrl(config, path)
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), init.timeoutMs ?? RUNNER_READ_TIMEOUT_MS)
  try {
    const response = await fetch(url, {
      method: init.method,
      headers: {
        ...(await signedHeaders(config, url, init.method)),
        'Content-Type': 'application/json',
      },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
      signal: abort.signal,
    })
    if (!response.ok) {
      throw await readErrorEnvelope(
        response,
        `Runner ${init.method} ${path} failed with ${response.status}`,
      )
    }
    return (await response.json()) as BuddyFlowEnvelope
  } finally {
    clearTimeout(timer)
  }
}

export interface BuddyFlowBootstrapInput {
  contactIdentifier: string
  customerEmail?: string
  customerPhone?: string
  customData: BuddyFlowCustomData
  message?: string
  attachments?: BuddyFlowAttachmentRef[]
  messageId?: string
}

/** Open a conversation. The customer's first message rides this call. */
export const bootstrapBuddyFlow = (
  input: BuddyFlowBootstrapInput,
  config: BuddyFlowConfig = getBuddyFlowConfig(),
): Promise<BuddyFlowEnvelope> =>
  runnerFetch(config, '/v1/chat/bootstrap', {
    method: 'POST',
    timeoutMs: BOOTSTRAP_TIMEOUT_MS,
    body: {
      app_id: config.appId,
      contact_identifier: input.contactIdentifier || null,
      customer_email: input.customerEmail?.toLowerCase() || null,
      customer_phone: input.customerPhone || null,
      custom_data: input.customData,
      ...(input.message != null ? { message: input.message } : {}),
      ...(input.attachments?.length ? { attachments: input.attachments } : {}),
      ...(input.messageId ? { message_id: input.messageId } : {}),
    },
  })

/** Re-attach a parked walk after a reload. */
export const getBuddyFlowState = (
  sessionId: string,
  config: BuddyFlowConfig = getBuddyFlowConfig(),
): Promise<BuddyFlowEnvelope> =>
  runnerFetch(config, `/v1/chat/session/${sessionId}/state`, { method: 'GET' })

/**
 * Answer a wait preset. Minimal on purpose: the console flow has no preset nodes
 * today, so this covers the one case where a bootstrap comes back `preset_wait`
 * and returns the next envelope for the caller to apply.
 */
export const sendBuddyFlowPresetTurn = (
  sessionId: string,
  reply: { message?: string; attachments?: BuddyFlowAttachmentRef[]; messageId?: string },
  config: BuddyFlowConfig = getBuddyFlowConfig(),
): Promise<BuddyFlowEnvelope> =>
  runnerFetch(config, `/v1/chat/session/${sessionId}/preset-turn`, {
    method: 'POST',
    body: {
      ...(reply.message != null ? { message: reply.message } : {}),
      ...(reply.attachments?.length ? { attachments: reply.attachments } : {}),
      ...(reply.messageId ? { message_id: reply.messageId } : {}),
    },
  })

export interface BuddyFlowTurnInput {
  agentUrl: string
  sessionId: string
  message?: string
  attachments?: BuddyFlowAttachmentRef[]
  messageId?: string
  /** Bubble-less generation trigger; the runner already holds the customer's words. */
  trigger?: boolean
  signal?: AbortSignal
}

/**
 * POST one turn and hand the caller the open response. Raw fetch with no client
 * timeout — a turn streams for as long as the model takes.
 */
export const openBuddyFlowTurn = async (
  input: BuddyFlowTurnInput,
  config: BuddyFlowConfig = getBuddyFlowConfig(),
): Promise<Response> => {
  // Signed over the ABSOLUTE agent-runner url, not the gateway base: the runner
  // matches the APP-ID header against the conversation's org.
  const url = `${resolveFlowBase(input.agentUrl, config.region)}/v1/chat/session/${input.sessionId}/turn`
  return fetch(url, {
    method: 'POST',
    headers: {
      ...(await signedHeaders(config, url, 'POST')),
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(
      input.trigger
        ? { trigger: true }
        : {
            ...(input.message != null ? { message: input.message } : {}),
            ...(input.attachments?.length ? { attachments: input.attachments } : {}),
            ...(input.messageId ? { message_id: input.messageId } : {}),
          },
    ),
    ...(input.signal ? { signal: input.signal } : {}),
  })
}

/** Decode a `data:` url back into a File the multipart upload can carry. */
export const dataUrlToFile = (dataUrl: string, fileName: string): File => {
  const [header, encoded] = dataUrl.split(',')
  const mimeType = header.match(/:(.*?);/)?.[1] ?? 'application/octet-stream'
  const binary = atob(encoded ?? '')
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new File([bytes], fileName, { type: mimeType })
}

/**
 * Multipart-upload one attachment and return the reference the flow carries. The
 * flow path never sends base64 — the runner and every action behind it expect a url.
 */
export const uploadBuddyFlowAttachment = async (
  file: { url: string; filename?: string; mediaType: string },
  config: BuddyFlowConfig = getBuddyFlowConfig(),
): Promise<BuddyFlowAttachmentRef> => {
  const fileName = file.filename || 'attachment'
  const url = `${resolveFlowBase(config.apiBaseUrl, config.region)}/v2/contacto-uploader/file`
  const body = new FormData()
  body.append('file', dataUrlToFile(file.url, fileName))

  // No Content-Type header: fetch has to mint the multipart boundary itself.
  const response = await fetch(url, {
    method: 'POST',
    headers: await signedHeaders(config, url, 'POST'),
    body,
  })
  if (!response.ok) {
    throw await readErrorEnvelope(response, `Attachment upload failed with ${response.status}`)
  }
  const payload = (await response.json()) as {
    data?: { file_url?: string; file_name?: string; file_type?: string }
    file_url?: string
    file_name?: string
    file_type?: string
  }
  const uploaded = payload.data ?? payload
  if (!uploaded.file_url) {
    throw new BuddyFlowError(response.status, 'UPLOAD_EMPTY', 'Upload returned no file url')
  }
  return {
    file_url: uploaded.file_url,
    file_name: uploaded.file_name || fileName,
    mime_type: uploaded.file_type || file.mediaType,
  }
}
